<template>
  <v-container id="networth" fluid tag="section">
    <WizardAlert section="netWorth" alertClass="mt-4 mb-1">
      <template v-slot:text>
        <p>{{ $t('views.netWorth.wizard.a') }}</p>
        <p>{{ $t('views.netWorth.wizard.b') }}</p>
        <p>{{ $t('views.netWorth.wizard.c') }}</p>
      </template>
    </WizardAlert>

    <SomePendingRecreatePosition :somePendingRecreatePosition="somePendingRecreatePosition" />

    <v-row>
      <v-col cols="12" lg="3" class="pb-0 mb-2">
        <div class="elevation-0 my-0 pt-1">
          <SummaryCard
            :title="$t('views.netWorth.total')"
            :loading="loading"
            :value="netWorthResult.total"
            :valueCurrency="user.baseCurrency"
          />
        </div>
      </v-col>

      <v-col cols="12" lg="9">
        <v-card
          class="elevation-0 mb-2 mt-0 pt-1 pb-5"
          :class="{ 'px-7': !isMobile, 'px-4': isMobile }"
        >
          <div class="items-flex justify-between" :class="{ 'mt-4': !isMobile, 'mt-2': isMobile }">
            <div>
              <a class="networth-edit-link mt-6 darkTextGrey--text" @click="editNetWorthRecipe">
                <v-icon
                  :color="$vuetify.theme.dark ? 'white' : 'darkTextGrey'"
                  size="16"
                  class="networth-edit-link-icon"
                >
                  mdi-cog
                </v-icon>
                <span :class="{ 'ml-1': !isMobile, 'white--text': $vuetify.theme.dark }">
                  {{
                    isMobile
                      ? $t('views.netWorth.configureRecipeMobile')
                      : $t('views.netWorth.configureRecipe')
                  }}
                </span>
              </a>
            </div>

            <WizardAlertActivator section="netWorth" />
          </div>
          <BarLoading v-if="loading" />
          <template v-else>
            <div v-for="(group, groupIdx) in netWorthResult.groups" :key="groupIdx" class="group">
              <div class="group-title" :class="{ 'is-mobile': isMobile }">
                <h1>{{ group.name }}</h1>
                <h1>
                  <currency :value="group.total" :currency="user.baseCurrency" />
                </h1>
              </div>

              <v-divider />

              <v-simple-table dense class="account-row" :class="{ 'is-mobile': isMobile }">
                <template v-slot:default>
                  <tbody>
                    <tr v-for="(account, accountIdx) in group.accounts">
                      <td>
                        <span class="account-row-name">{{ account.name }}</span>
                      </td>
                      <td class="text-right">
                        <div class="items-flex align-center justify-end">
                          <currency :value="account.value" :currency="user.baseCurrency" />
                          <span class="ml-2">({{ round(account.weight).toLocaleString() }}%)</span>
                          <v-tooltip top max-width="300px">
                            <template v-slot:activator="{ on, attrs }">
                              <v-avatar
                                v-bind="attrs"
                                size="7"
                                :color="account.type !== 'custom' ? 'primary' : 'softTextGrey'"
                                class="ml-2"
                                v-on="on"
                              />
                            </template>
                            <span>
                              {{ $t(`views.netWorth.recipeModal.accountType.${account.type}`) }}
                            </span>
                          </v-tooltip>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </template>
        </v-card>

        <NetWorthCharts :netWorthResult="netWorthResult" />

        <NetWorthRecipeModal
          :isModalActive.sync="isNetWorthRecipeModalActive"
          :netWorthRecipe="netWorthRecipe"
          @saved="initNetWorth"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapState } from 'vuex'
import { getUserNetWorth, updateUserNetWorth, getPositionsLean } from '@/lib/api.js'
import { round } from '@/lib/utils/math.js'
import gtag from '@/lib/gtag'
import BarLoading from '@/views/components/core/BarLoading.vue'
import Currency from '@/views/components/common/Currency'
import NetWorthRecipeModal from '@/views/components/netWorth/NetWorthRecipeModal'
import NetWorthCharts from '@/views/components/netWorth/NetWorthCharts'
import WizardAlert from '@/views/components/common/WizardAlert'
import WizardAlertActivator from '@/views/components/common/WizardAlertActivator'
import SummaryCard from '@/views/components/dashboardDetail/summary/SummaryCard'
import visibilityMixin from '@/views/components/core/visibilityMixin'
import SomePendingRecreatePosition from '@/views/components/common/positions/SomePendingRecreatePosition.vue'

export default {
  components: {
    BarLoading,
    Currency,
    NetWorthRecipeModal,
    NetWorthCharts,
    WizardAlert,
    WizardAlertActivator,
    SummaryCard,
    SomePendingRecreatePosition,
  },
  mixins: [visibilityMixin],
  data() {
    return {
      loading: false,
      netWorthResult: {},
      netWorthRecipe: {},
      isNetWorthRecipeModalActive: false,
      somePendingRecreatePosition: false,
      loadTimeout: null,
    }
  },
  computed: {
    ...mapState(['user']),
    isMobile() {
      return this.$vuetify.breakpoint.xs
    },
  },
  mounted() {
    this.initNetWorth()
  },
  async destroyed() {
    clearTimeout(this.loadTimeout)
  },
  methods: {
    round,
    getDefaultNetWorthRecipe() {
      return JSON.parse(
        JSON.stringify({
          groups: [
            {
              name: this.$t('views.netWorth.default.invested'),
              accounts: [
                {
                  name: this.$t('views.netWorth.default.stocks'),
                  data: { types: ['stock'] },
                  type: 'auto',
                },
                {
                  name: this.$t('views.netWorth.default.funds'),
                  data: { types: ['fund'] },
                  type: 'auto',
                },
                {
                  name: this.$t('views.netWorth.default.cash'),
                  data: { types: ['cash'] },
                  type: 'auto',
                },
              ],
            },
            {
              name: this.$t('views.netWorth.default.liquid'),
              accounts: [
                {
                  name: this.$t('views.netWorth.default.buffer'),
                  data: { value: 0 },
                  type: 'custom',
                },
                {
                  name: this.$t('views.netWorth.default.saving'),
                  data: { value: 0 },
                  type: 'custom',
                },
              ],
            },
          ],
        })
      )
    },
    async configureDefaultNetWorthRecipe() {
      await updateUserNetWorth({ data: { recipe: this.getDefaultNetWorthRecipe() } })
      await this.initNetWorth()
    },
    async initNetWorth() {
      this.loading = true
      try {
        const positions = await getPositionsLean({
          filter: {},
        })
        this.somePendingRecreatePosition = positions.data.some((item) => item.pendingRecreate)

        const netWorth = await getUserNetWorth()

        this.$set(this, 'netWorthResult', netWorth.data.result)
        if (!this.netWorthResult.groups) {
          await this.configureDefaultNetWorthRecipe()
          return
        }

        this.$set(this, 'netWorthRecipe', netWorth.data.recipe)

        clearTimeout(this.loadTimeout)
        if (this.somePendingRecreatePosition) {
          this.loadTimeout = setTimeout(() => {
            this.initNetWorth()
          }, 5000)
        }
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.loading = false
    },
    editNetWorthRecipe() {
      this.isNetWorthRecipeModalActive = true

      gtag.pageview({
        pagePath: `/netWorth/configure`,
        pageTitle: `netWorth|configure`,
        pageContentGroup: 'modal',
      })
    },
  },
}
</script>

<style lang="scss">
#networth {
  .items-flex {
    display: flex;
    &.align-center {
      align-items: center;
    }
    &.justify-end {
      justify-content: flex-end;
    }
    &.justify-between {
      justify-content: space-between;
    }
  }
  h1 {
    font-size: 17px;
    font-weight: 400;
  }
  .theme--dark {
    .group {
      .group-title {
        background-color: #292929;
      }
    }
  }
  .group {
    margin-top: 20px;
    margin-bottom: 10px;
    .group-title {
      margin: 0px;
      padding: 10px 17px 10px 10px;
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      background-color: #f7f8fb;
      h1 {
        font-size: 19px;
        font-weight: 500;
      }

      &.is-mobile {
        padding: 8px 8px 8px 8px;
        h1 {
          font-size: 17px;
        }
      }
    }
  }
  .account-row {
    margin-top: 10px;
    td {
      font-size: 16px;
    }
    &.is-mobile {
      td {
        font-size: 14px;
        padding: 5px;
      }
    }
  }

  .networth-edit-link {
    font-size: 15px;
    font-weight: 500;
    .networth-edit-link-icon {
      margin-bottom: 2px;
    }
  }
}
</style>
